import { Reducer } from 'redux';
import {
  ListStacksResponseItemPaginatedResponse,
  MeasurementCardResponse,
  StackCoordinateResponse,
  StackFilterOptionsResponse,
  StackMapInfoResponse,
  StacksQueryPagedRequest,
} from '../.generated/api';
import { IStackQuery } from './models/IStackQuery';

export const UPDATE_STACK_MEASUREMENT_CARD = 'UPDATE_STACK_MEASUREMENT_CARD';
export const UPDATE_STACK_LIST = 'UPDATE_STACK_LIST';
export const UPDATE_STACK_COORDINATES = 'UPDATE_STACK_COORDINATES';
export const UPDATE_STACK_MAP_INFO = 'UPDATE_STACK_MAP_INFO';
export const SIGNAL_STACK_LIST_LOADING = 'SIGNAL_STACK_LIST_LOADING';
export const SIGNAL_STACK_COORDINATES_LOADING =
  'SIGNAL_STACK_COORDINATES_LOADING';
export const UPDATE_STACK_FILTER_OPTIONS = 'UPDATE_STACK_FILTER_OPTIONS';
export const STACK_DELETE = 'STACK_DELETE';
export const STACK_DELETE_SUCCESS = 'STACK_DELETE_SUCCESS';
export const STACK_DELETE_ERROR = 'STACK_DELETE_ERROR';
export const UPDATE_ARCHIVE_DOWNLOAD_PROGRESS =
  'UPDATE_ARCHIVE_DOWNLOAD_PROGRESS';
export const UPDATE_ACCUMULATED_CSV_DOWNLOAD_PROGRESS =
  'UPDATE_ACCUMULATED_CSV_DOWNLOAD_PROGRESS';
export const UPDATE_ACCUMULATED_CSV_DOWNLOAD_ALL_PROGRESS =
  'UPDATE_ACCUMULATED_CSV_DOWNLOAD_ALL_PROGRESS';

export interface StackListState {
  measurementCard?: MeasurementCardResponse | null;
  stacks: ListStacksResponseItemPaginatedResponse | null;
  updatedAt?: Date; // latest stack query timestamp
  coordinates: StackCoordinateResponse[];
  mapInfo?: StackMapInfoResponse;
  isLoadingStacks: boolean;
  isLoadingCoordinates: boolean;
  filters: IStackQuery;
  filtersApplied: number;
  stacksDeleting: string[];
  filterOptions?: StackFilterOptionsResponse | null;
  archiveDownloadProgress?: number; // negative means progress without total, [0-100] means percentage progress.
  accumulatedCsvDownloadProgress?: number; // negative means progress without total, [0-100] means percentage progress.
  accumulatedCsvDownloadAllProgress?: number; // negative means progress without total, [0-100] means percentage progress.
}

export interface UpdateStackMeasurementCard {
  type: typeof UPDATE_STACK_MEASUREMENT_CARD;
  measurementCard?: MeasurementCardResponse | null;
}

export interface UpdateStackList {
  type: typeof UPDATE_STACK_LIST;
  stacks: ListStacksResponseItemPaginatedResponse | null;
  filters: IStackQuery;
}

export interface UpdateStackCoordinates {
  type: typeof UPDATE_STACK_COORDINATES;
  coordinates: StackCoordinateResponse[];
}

export interface UpdateStackMapInfo {
  type: typeof UPDATE_STACK_MAP_INFO;
  mapInfo?: StackMapInfoResponse;
}

export interface SignalStackListLoading {
  type: typeof SIGNAL_STACK_LIST_LOADING;
}

export interface SignalStackCoordinatesLoading {
  type: typeof SIGNAL_STACK_COORDINATES_LOADING;
}

export interface UpdateStackFilterOptions {
  type: typeof UPDATE_STACK_FILTER_OPTIONS;
  filterOptions?: StackFilterOptionsResponse | null;
}

export interface StackDelete {
  type: typeof STACK_DELETE;
  stackId: string;
}

export interface StackDeleteSuccess {
  type: typeof STACK_DELETE_SUCCESS;
  stackId: string;
}

export interface StackDeleteError {
  type: typeof STACK_DELETE_ERROR;
  stackId: string;
}

export interface UpdateArchiveDownloadProgress {
  type: typeof UPDATE_ARCHIVE_DOWNLOAD_PROGRESS;
  progress?: number;
}

export interface UpdateAccumulatedCsvDownloadProgress {
  type: typeof UPDATE_ACCUMULATED_CSV_DOWNLOAD_PROGRESS;
  progress?: number;
}

export interface UpdateAccumulatedCsvDownloadAllProgress {
  type: typeof UPDATE_ACCUMULATED_CSV_DOWNLOAD_ALL_PROGRESS;
  progress?: number;
}

const countFiltersApplied = (filters: StacksQueryPagedRequest) => {
  let count = 0;
  filters.states && (count += filters.states.length);
  filters.sawmillIds && (count += filters.sawmillIds.length);
  filters.registrators && (count += filters.registrators.length);
  filters.surveyors && (count += filters.surveyors.length);
  filters.search && filters.search.length > 0 && count++;
  filters.treeSpecies && (count += filters.treeSpecies.length);
  filters.assortments && (count += filters.assortments.length);
  filters.areaNumber && count++;
  filters.assortmentLengthMin && filters.assortmentLengthMax && count++;
  filters.fullMeasurement != null && count++;
  filters.syncWithLesnikPlus != null && count++;
  return count;
};

type StackListActionTypes =
  | UpdateStackMeasurementCard
  | UpdateStackList
  | UpdateStackCoordinates
  | UpdateStackMapInfo
  | SignalStackListLoading
  | SignalStackCoordinatesLoading
  | UpdateStackFilterOptions
  | StackDelete
  | StackDeleteSuccess
  | StackDeleteError
  | UpdateArchiveDownloadProgress
  | UpdateAccumulatedCsvDownloadProgress
  | UpdateAccumulatedCsvDownloadAllProgress;

const stackListReducer: Reducer<StackListState, StackListActionTypes> = (
  state: StackListState | undefined,
  incomingAction: StackListActionTypes,
): StackListState => {
  if (state === undefined) {
    return {
      measurementCard: null,
      stacks: null,
      filterOptions: null,
      coordinates: [],
      isLoadingStacks: false,
      isLoadingCoordinates: false,
      stacksDeleting: [],
      filters: {
        departmentIds: [],
        showMap: 'true',
      },
      filtersApplied: 0,
    };
  }

  switch (incomingAction.type) {
    case UPDATE_STACK_MEASUREMENT_CARD:
      return {
        ...state,
        measurementCard: incomingAction.measurementCard,
      };
    case UPDATE_STACK_LIST:
      return {
        ...state,
        stacks: incomingAction.stacks,
        filters: incomingAction.filters,
        filtersApplied: countFiltersApplied(incomingAction.filters),
        isLoadingStacks: false,
        updatedAt: new Date(),
      };
    case UPDATE_STACK_COORDINATES:
      return {
        ...state,
        coordinates: incomingAction.coordinates,
        isLoadingCoordinates: false,
      };
    case UPDATE_STACK_MAP_INFO:
      return {
        ...state,
        mapInfo: incomingAction.mapInfo,
      };
    case SIGNAL_STACK_LIST_LOADING:
      return {
        ...state,
        isLoadingStacks: true,
      };
    case SIGNAL_STACK_COORDINATES_LOADING:
      return {
        ...state,
        isLoadingCoordinates: true,
      };
    case UPDATE_STACK_FILTER_OPTIONS:
      return {
        ...state,
        filterOptions: incomingAction.filterOptions,
      };
    case STACK_DELETE: {
      if (state.stacksDeleting.includes(incomingAction.stackId)) {
        return state;
      }

      return {
        ...state,
        stacksDeleting: state.stacksDeleting.concat(incomingAction.stackId),
      };
    }
    case STACK_DELETE_SUCCESS:
      return state.stacks
        ? {
            ...state,
            stacks: {
              ...state.stacks,
              data:
                state.stacks.data.filter(
                  (stack) => stack.stackId !== incomingAction.stackId,
                ) ?? [],
            },
            stacksDeleting: state.stacksDeleting.filter(
              (x) => x !== incomingAction.stackId,
            ),
          }
        : state;
    case STACK_DELETE_ERROR:
      return {
        ...state,
        stacksDeleting: state.stacksDeleting.filter(
          (x) => x !== incomingAction.stackId,
        ),
      };
    case UPDATE_ARCHIVE_DOWNLOAD_PROGRESS:
      return {
        ...state,
        archiveDownloadProgress: incomingAction.progress,
      };
    case UPDATE_ACCUMULATED_CSV_DOWNLOAD_PROGRESS:
      return {
        ...state,
        accumulatedCsvDownloadProgress: incomingAction.progress,
      };
    case UPDATE_ACCUMULATED_CSV_DOWNLOAD_ALL_PROGRESS:
      return {
        ...state,
        accumulatedCsvDownloadAllProgress: incomingAction.progress,
      };

    default:
      return state;
  }
};

export { stackListReducer };
